<template>
  <div class="new-release" v-show="cars.length > 0" @click.stop>
    <div class="new-release__container">
      <!-- 标题栏 -->
      <div class="new-release__title">
        <div class="new-release__heading">New Release Rank</div>
        <div class="new-release__view" @click="gotoAll()">
          <div class="new-release__view-all">View All</div>
          <img src="@/assets/view-all.svg" class="new-release__icon" alt="view all icon">
        </div>
      </div>

      <!-- 内容区域 -->
      <div class="new-release__content">
        <!-- 正常内容显示 -->
        <div class="new-release__cards">
          <div v-for="(car, index) in displayedCars" :key="`car-${index}`" class="new-release__card"
            @click="gotoDetail(car)" :data-empty="car.isEmpty">
            <!-- 左侧信息区域 -->
            <div class="new-release__info">
              <!-- 车辆品牌和名称 -->
              <div class="new-release__info-header">
                <div class="new-release__brand">{{ car.brand }}</div>
                <div class="new-release__model">{{ car.model }}</div>
              </div>

              <!-- 发布时间 -->
              <div class="new-release__release-time">
                <div class="new-release__time">{{ car.releaseTime }}</div>
              </div>
            </div>

            <!-- 右侧图片区域 -->
            <div class="new-release__image-wrapper">
              <img :src="car.image" class="new-release__pic" alt="car image">
            </div>
          </div>
        </div>

        <!-- 导航按钮和指示器 - 仅在PC端显示且有多页时显示 -->
        <div class="new-release__nav" v-if="deviceType !== 'mobile' && totalPages > 1">
          <div class="new-release__nav-arrow new-release__nav-prev" @click="prevPage">
            <img src="@/assets/arrow-left-blue.svg" alt="Previous">
          </div>

          <div class="new-release__nav-dots">
            <div v-for="n in totalPages" :key="`dot-${n}`" class="new-release__nav-dot"
              :class="{'new-release__nav-dot--active': currentPage === n}" @click="goToPage(n)">
            </div>
          </div>

          <div class="new-release__nav-arrow new-release__nav-next" @click="nextPage">
            <img src="@/assets/arrow-right-blue.svg" alt="Next">
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'CarNewRelease',
  computed: {
    ...mapGetters({
      deviceType: 'deviceType'
    }),
    // 计算当前显示的卡片
    displayedCars () {
      // 移动端展示所有车辆，不做分页
      if (this.deviceType === 'mobile') {
        return this.cars;
      }

      // PC端保持原来的分页逻辑
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const items = this.cars.slice(start, start + this.itemsPerPage);

      // 如果不足6个，填充到6个以保持布局一致 (调整为6个，每行3个)
      while (items.length < this.itemsPerPage && items.length > 0) {
        items.push({ ...items[0], isEmpty: true });
      }

      return items;
    },
    // 计算总页数
    totalPages () {
      return Math.ceil(this.cars.length / this.itemsPerPage);
    }
  },
  data () {
    return {
      currentPage: 1,
      itemsPerPage: 6, // 调整为6个，显示两行，每行3个
      cars: [],
      loading: false,
      error: null
    }
  },
  mounted () {
    this.fetchNewRelease();
  },
  methods: {
    // 获取新发布车型数据
    async fetchNewRelease () {
      this.loading = true;
      this.error = null;
      try {
        // 从store获取域名配置的主题过滤条件
        const themeFilters = this.$store.getters.domainConfig?.themeFilters || {};
        // 获取webIndex
        const webIndex = this.$store.getters.domainConfig?.webIndex || 1;

        // 调用global.js中的getRecommendedNewReleaseRank方法获取数据
        const params = {
          ...themeFilters,
          webIndex
        };
        const res = await this.$global.server.getRecommendedNewReleaseRank(params);
        if (res.code === 200) {
          // 处理API返回的数据
          const apiData = res.data || [];
          const processedData = apiData.map(item => {
            // 从photos字段提取第一张图片作为封面
            const photos = item.photos ? item.photos.split(',') : [];
            const firstPhoto = photos.length > 0 ? photos[0] : require('@/assets/car-recommend-img.png');

            // 格式化发布时间
            let releaseTime = item.marketTime || item.year || '-';
            if (releaseTime && releaseTime.length >= 7) {
              releaseTime = releaseTime.substring(0, 7).replace('-', '.');
            }

            // 返回处理后的数据，映射字段名称
            return {
              id: item.id,
              model: item.name, // API中是name字段，映射为model
              brand: item.brand,
              logo: item.logoUrl || require('@/assets/car-logo.svg'), // 优先使用接口返回的logoUrl字段，没有则使用默认logo
              year: item.year || item.marketTime?.substring(0, 4) || '-',
              category: item.purpose || '-', // 使用purpose字段作为category
              bodyType: item.type || '-', // 使用type字段作为bodyType
              price: this.formatPrice(item.leastPrice, item.mostPrice) || item.price || '-',
              image: item.cover || firstPhoto, // 使用cover字段，如果没有则使用firstPhoto
              releaseTime: releaseTime,
              isEmpty: false
            };
          });

          this.cars = processedData;
          console.log('[CarNewRelease] 获取新车发布排名:', this.cars.length);
          // 重置当前页为第一页
          this.currentPage = 1;
        } else {
          // 如果没有数据，则设置为空数组
          this.cars = [];
          console.log('[CarNewRelease] 没有新车发布排名数据');
        }
      } catch (error) {
        console.error('[CarNewRelease] 获取新车发布排名错误:', error);
        this.error = error.message || 'Failed to get new release rank';
        this.cars = [];
      } finally {
        this.loading = false;
      }
    },

    // 格式化价格显示
    formatPrice (leastPrice, mostPrice) {
      if (!leastPrice && !mostPrice) return null;

      const formatNumber = (num) => {
        if (!num) return null;
        // 将价格格式化为$xx,xxx格式
        return '$' + num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      };

      if (leastPrice && mostPrice) {
        // 有最低价和最高价，显示价格区间
        return `${formatNumber(leastPrice)}-${formatNumber(mostPrice)}`;
      } else if (leastPrice) {
        // 只有最低价
        return `>${formatNumber(leastPrice)}`;
      } else if (mostPrice) {
        // 只有最高价
        return `<${formatNumber(mostPrice)}`;
      }

      return null;
    },

    // 上一页
    prevPage () {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    // 下一页
    nextPage () {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    // 跳转到指定页
    goToPage (page) {
      this.currentPage = page;
    },

    // 跳转到全部车辆页面
    gotoAll () {
      // 获取当前路由的channel参数
      const currentChannel = this.$router.currentRoute.params.channel;

      // 如果存在channel参数，则构建带channel的路径
      if (currentChannel) {
        // 构建完整URL
        const baseUrl = window.location.origin;
        // 直接获取当前URL的查询字符串，包括?前缀
        const fullQueryString = window.location.search;
        const fullPath = `${baseUrl}/${currentChannel}/car-list${fullQueryString}`;

        // 使用location跳转
        window.location.href = fullPath;
      } else {
        // 没有channel参数，使用常规router跳转
        this.$router.push({
          path: '/car-list'
        });
      }
    },

    // 跳转到车辆详情页面
    gotoDetail (car) {
      if (!car || !car.id || car.isEmpty) {
        console.warn('[NewRelease] Missing car data for detail page');
        return;
      }

      // 获取当前路由的channel参数
      const currentChannel = this.$router.currentRoute.params.channel;

      // 格式化车辆名称，去除空格，用连字符替代
      const formattedName = encodeURIComponent((car.model || '').replace(/\s+/g, '-'));

      // 如果存在channel参数，则构建带channel的路径
      if (currentChannel) {
        // 获取当前的查询参数
        const currentQuery = this.$route.query;
        const queryString = new URLSearchParams(currentQuery).toString();
        const fullQueryString = queryString ? `?${queryString}` : '';

        // 构建完整URL
        const baseUrl = window.location.origin;
        const fullPath = `${baseUrl}/${currentChannel}/cars/${formattedName}/${car.id}${fullQueryString}`;

        // 使用location跳转，保留所有参数
        window.location.href = fullPath;
      } else {
        // 没有channel参数，使用常规router跳转
        this.$router.push({
          path: `/cars/${formattedName}/${car.id}`
        });
      }
    }
  }
}
</script>

<style lang="scss" >
@import '~@/css/variables.scss';

.new-release {
	padding: rem(60) 0;
	background: #ffffff;

	&__container {
		padding: 0 rem(120);
	}

	&__title {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: rem(20);
	}

	&__heading {
		font-size: rem(24);
		line-height: rem(24);
		font-weight: 700;
		color: #141414;
	}

	&__view {
		display: flex;
		align-items: center;
		gap: rem(12);
		cursor: pointer;
	}

	&__view-all {
		font-size: rem(14);
		line-height: rem(14);
		font-weight: 600;
		color: var(--theme-color);
	}

	&__icon {
		width: rem(16);
		height: rem(16);
	}

	&__content {
		display: flex;
		flex-direction: column;
		width: 100%;
		position: relative;
	}

	&__cards {
		display: flex;
		flex-wrap: wrap;
		gap: rem(20);
		justify-content: space-between;
		align-content: flex-start;
	}

	&__card {
		background: #ffffff;
		border-radius: rem(24);
		border: 1px solid #e9e9e9;
		padding: rem(20);
		display: flex;
		flex-direction: row;
		gap: rem(20);
		flex: 0 0 calc((100% - rem(40)) / 3); /* 使每个子元素占1/3的宽度 */
		position: relative;
		cursor: pointer;
		transition: transform 0.3s ease, box-shadow 0.3s ease;

		&:hover {
			transform: translateY(-4px);
			box-shadow: 0 rem(8) rem(16) rgba(0, 0, 0, 0.1);
		}

		&[data-empty='true'] {
			visibility: hidden;
		}

		&--badge {
			background: rgba(var(--theme-color-r), var(--theme-color-g), var(--theme-color-b), 0.1);
			border-radius: rem(8);
			padding: rem(4) rem(8);
			display: flex;
			align-items: center;
			gap: rem(8);

			span {
				color: var(--theme-color);
				font-size: rem(14);
				line-height: rem(14);
				font-weight: 600;
			}
		}
	}

	&__info {
		padding: rem(4) 0;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		width: rem(120);
		height: rem(138);
	}

	&__info-header {
		padding: rem(8);
		display: flex;
		flex-direction: column;
		gap: rem(12);
	}

	&__brand {
		color: #777777;
		font-size: rem(14);
		line-height: rem(14);
		font-weight: 400;
	}

	&__model {
		color: #141414;
		font-size: rem(16);
		line-height: rem(20);
		font-weight: 600;
	}

	&__release-time {
		background: rgba(var(--theme-color-r), var(--theme-color-g), var(--theme-color-b), 0.1);
		border-radius: rem(8);
		padding: rem(10) 0;
		display: flex;
		justify-content: center;
		align-items: center;
		width: 100%;
	}

	&__time {
		color: var(--theme-color);
		font-size: rem(18);
		line-height: rem(20);
		font-weight: 700;
		text-align: center;
	}

	&__image-wrapper {
		flex: 1;
		display: flex;
		flex-direction: column;
	}

	&__pic {
		border-radius: rem(16);
		border: 1px solid #ffffff;
		width: 100%;
		height: rem(138);
		object-fit: cover;
	}

	&__nav {
		display: flex;
		justify-content: flex-start;
		align-items: center;
		margin-top: rem(20);
		gap: rem(20);
	}

	&__nav-dots {
		display: flex;
		gap: rem(8);
		align-items: center;
	}

	&__nav-dot {
		width: rem(8);
		height: rem(8);
		background-color: #e0e0e0;
		border-radius: 50%;
		cursor: pointer;
		transition: all 0.3s ease;

		&--active {
			background-color: var(--theme-color);
		}
	}

	&__nav-arrow {
		width: rem(40);
		height: rem(40);
		border-radius: 8px;
		border: 1px solid rgba(var(--theme-color-r), var(--theme-color-g), var(--theme-color-b), 0.1);
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: all 0.3s ease;

		&:hover {
			border: 1px solid rgba(var(--theme-color-r), var(--theme-color-g), var(--theme-color-b), 0.6);
		}

		img {
			width: rem(20);
			height: rem(20);
			filter: hue-rotate(190deg); // 将蓝色图标转为橙色
		}
	}

	&__indicator {
		// ... existing code ...

		&--active {
			background-color: var(--theme-color);
		}
	}

	&__price-button {
		// ... existing code ...
		border: 1px solid rgba(var(--theme-color-r), var(--theme-color-g), var(--theme-color-b), 0.1);

		&:hover {
			border: 1px solid rgba(var(--theme-color-r), var(--theme-color-g), var(--theme-color-b), 0.6);
		}
	}
}

// 移动端适配
.detect_deviece_mobile {
	.new-release {
		--fontsize-base: #{$vw_fontsize_mobile};
		font-size: calc(var(--fontsize-base) / #{$vw_design_mobile} * 100vw);
	}

	.new-release {
		padding: rem(30) rem(16);

		&__container {
			padding: 0;
		}

		&__cards {
			display: flex;
			flex-wrap: nowrap;
			overflow-x: auto;
			gap: rem(8);

			padding-bottom: rem(10);
			scrollbar-width: none;
			-ms-overflow-style: none;
			&::-webkit-scrollbar {
				display: none;
			}
			margin: 0 rem(-16);
			padding: 0 rem(16);
			width: calc(100% + rem(32));
		}

		&__image-wrapper {
			order: 1;
		}
		&__pic {
			height: rem(202);
		}

		&__card {
			flex: 0 0 rem(343);
			min-width: rem(343);
			margin-bottom: 0;
			flex-direction: column;
			gap: rem(16);
		}
		&__info {
			order: 2;
			padding: 0;
			width: 100%;
			height: fit-content;
			gap: rem(12);
			&-header {
				padding: 0 8px;
				width: 100%;
			}
		}

		&__heading {
			font-size: rem(24);
			line-height: rem(24);
		}

		&__view-all {
			font-size: rem(14);
			line-height: rem(14);
		}

		&__icon {
			width: rem(14);
			height: rem(14);
		}
	}
}

// 响应式样式 - PC设备
.detect_deviece_pc,
.detect_deviece_desktop {
	.new-release {
		--fontsize-base: #{$vw_fontsize_pc};
		font-size: calc(var(--fontsize-base) / #{$vw_design_pc} * 100vw);
	}
}
</style> 